// Reads the install state captured at module load. See installCapture.ts for
// why the listener cannot live in a component.
import { useCallback, useEffect, useState } from 'react';
import { installMode, isStandalone, type InstallMode } from './installState';
import {
  consumePrompt, getCapturedPrompt, subscribeToInstallState, wasInstalledThisSession,
} from './installCapture';

export interface InstallApi {
  mode: InstallMode;
  /** Fires the real browser prompt. Resolves to the user's choice, or
   *  'unavailable' when there was never an event to fire. */
  promptInstall: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
}

export const useInstallPrompt = (): InstallApi => {
  const [, bump] = useState(0);
  const [standalone, setStandalone] = useState(() => isStandalone());

  useEffect(() => subscribeToInstallState(() => bump((n) => n + 1)), []);

  useEffect(() => {
    const mq = window.matchMedia?.('(display-mode: standalone)');
    const onDisplay = () => setStandalone(isStandalone());
    mq?.addEventListener?.('change', onDisplay);
    return () => mq?.removeEventListener?.('change', onDisplay);
  }, []);

  const promptInstall = useCallback(async () => {
    const event = consumePrompt();
    if (!event) return 'unavailable' as const;
    await event.prompt();
    const { outcome } = await event.userChoice;
    return outcome;
  }, []);

  return {
    mode: installMode({
      hasPromptEvent: getCapturedPrompt() !== null,
      standalone: standalone || wasInstalledThisSession(),
      ua: typeof navigator === 'undefined' ? '' : navigator.userAgent,
    }),
    promptInstall,
  };
};
